import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MapView } from '@/components/MapView';
import { ConnectionStatus, useOnline } from '@/components/ConnectionStatus';
import { HoldSOSButton } from '@/components/HoldSOSButton';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { useT, tStatic } from '@/lib/i18n';
import { watchPosition, clearWatch, getCurrentPosition } from '@/lib/geo';
import { getRoute, remainingMinutes, distanceMeters, distanceToRoute } from '@/lib/route';
import { queueLocation, flushLocations, getCachedRide, cacheRide, syncPendingRides } from '@/lib/offline';
import { elapsed, timeAgo, VEHICLE_LABELS, smsHref } from '@/lib/format';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Share2, ShieldCheck, Copy, CheckCircle2, ChevronLeft, Loader2, EyeOff, WifiOff, MapPin, MailCheck, AlertTriangle, MessageCircle, Hash, Camera, ChevronRight } from 'lucide-react';

const POST_INTERVAL = 12000;
const ACCURACY_LIMIT = 100; // metres — ignore fixes worse than this once we have a good one
const GOOD_FIX_TTL = 20000; // ms — after this long without a precise fix, accept weaker ones so the dot never freezes

export default function ActiveRide() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const online = useOnline();
  const setActiveRideId = useApp((s) => s.setActiveRideId);
  const setSosRideId = useApp((s) => s.setSosRideId);
  const setPendingRating = useApp((s) => s.setPendingRating);

  const [ride, setRide] = useState(null);
  const [pos, setPos] = useState(null);
  const [path, setPath] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [weak, setWeak] = useState(false);
  const [geoError, setGeoError] = useState(null); // null | 'denied' | 'unavailable'
  const [routeCoords, setRouteCoords] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);
  const [offRouteDismissed, setOffRouteDismissed] = useState(false);
  const [plate, setPlate] = useState('');
  const [plateBusy, setPlateBusy] = useState(false);
  const [detailsDismissed, setDetailsDismissed] = useState(false);

  const watchId = useRef(null);
  const lastPost = useRef(0);
  const lastGood = useRef(0);
  const posRef = useRef(null);

  // Post a fix now (falls back to an offline queue). Shared by the GPS watcher
  // and the manual "Retry" button, so it lives at component scope.
  const sendNow = useCallback(async (p) => {
    if (!p) return;
    lastPost.current = Date.now();
    if (navigator.onLine) {
      try {
        await api.postLocation(id, p);
        await flushLocations(id, (pt) => api.postLocation(id, pt));
        setPath((prev) => [...prev, { ...p, recorded_at: new Date().toISOString() }]);
      } catch (err) { console.warn('postLocation failed, queued for retry:', err); await queueLocation(id, p); }
    } else { await queueLocation(id, p); }
  }, [id]);

  const handleGeoErr = useCallback((err) => {
    setGeoError((err && err.code === 1) ? 'denied' : 'unavailable');
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.getRide(id);
      setRide(r);
      cacheRide(r);
      if (r.status === 'completed' || r.status === 'cancelled') setActiveRideId(null);
      else setActiveRideId(r.id);
      const locs = await api.getLocations(id);
      setPath(locs);
      if (locs.length) setPos(locs[locs.length - 1]);
    } catch (e) {
      // Offline or not-yet-synced ride: fall back to the locally cached copy so
      // the active-ride UI still works (coordinates + last-known + offline banner).
      const cached = await getCachedRide(id);
      if (cached) {
        setRide(cached);
        if (cached.status !== 'completed' && cached.status !== 'cancelled') setActiveRideId(cached.id);
      } else {
        toast.error(tStatic('ar_load_err'));
      }
    }
  }, [id, setActiveRideId]);

  useEffect(() => { load(); }, [load]);

  // When connectivity returns, push any offline-created ride + queued GPS, then refresh.
  useEffect(() => {
    const onOnline = () => {
      syncPendingRides(
        (payload) => api.createRide(payload),
        (rideId, pt) => api.postLocation(rideId, pt),
      ).then(() => load()).catch((e) => console.debug('sync on online failed:', e));
    };
    window.addEventListener('online', onOnline);
    if (navigator.onLine) onOnline(); // also sync if already online on mount
    return () => window.removeEventListener('online', onOnline);
  }, [load]);
  useEffect(() => { const tm = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(tm); }, []);

  // Fetch the planned road route (pickup -> destination) once.
  useEffect(() => {
    if (!ride || ride.destination_lat == null) return;
    const originPt = (ride.origin_lat != null)
      ? { latitude: ride.origin_lat, longitude: ride.origin_lng }
      : (path[0] ? { latitude: path[0].latitude, longitude: path[0].longitude } : null);
    const destPt = { latitude: ride.destination_lat, longitude: ride.destination_lng };
    if (!originPt) return;
    getRoute(originPt, destPt).then((r) => {
      if (r && r.coords) { setRouteCoords(r.coords); setRouteInfo(r); }
      else { setRouteCoords([originPt, destPt]); setRouteInfo(null); } // straight fallback
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride?.id, ride?.destination_lat]);

  const originPt = ride && ride.origin_lat != null ? { latitude: ride.origin_lat, longitude: ride.origin_lng } : null;
  const destPt = ride && ride.destination_lat != null ? { latitude: ride.destination_lat, longitude: ride.destination_lng } : null;
  const rideStatus = ride?.status; // primitive gate for the tracking effect (stable across ride-object identity changes)
  const etaMin = remainingMinutes(routeInfo, pos);
  const etaText = etaMin == null ? null : (etaMin <= 1 ? t('eta_soon') : `${t('eta_about')} ${etaMin} ${t('eta_min')}`);

  // Destination-powered safety signals.
  const distToDest = destPt && pos ? distanceMeters(destPt, pos) : null;
  const nearDest = distToDest != null && distToDest <= 150; // ~arrived
  const routeDeviation = distanceToRoute(routeInfo, pos);
  const offRoute = !nearDest && routeDeviation != null && routeDeviation > 500;
  const destSuffix = ride?.destination_text ? ` ${t('sms_heading_to').replace('{d}', ride.destination_text)}` : '';
  // A tappable Google Maps pin of the rider's current GPS fix — so contacts
  // without email still get an exact location straight in the text message.
  const mapsPinFor = (p) => (p && p.latitude != null) ? `https://www.google.com/maps?q=${p.latitude},${p.longitude}` : '';
  const locSuffix = pos ? ` ${t('sms_map_pin')} ${mapsPinFor(pos)}` : '';

  useEffect(() => {
    if (!rideStatus || rideStatus === 'completed' || rideStatus === 'cancelled') return;
    // Accept a GPS fix. Weak fixes still update the dot unless we very recently had
    // a precise one (anti-jitter) — so live tracking never freezes on so-so GPS.
    const onFix = (p, isInitial = false) => {
      const acceptable = p.accuracy == null || p.accuracy <= ACCURACY_LIMIT;
      setWeak(!acceptable);
      setGeoError(null); // we have a signal now — clear any earlier error
      const nowT = Date.now();
      if (!isInitial && !acceptable && posRef.current && (nowT - lastGood.current) < GOOD_FIX_TTL) return;
      setPos(p); posRef.current = p;
      if (acceptable) lastGood.current = nowT;
      if (isInitial || nowT - lastPost.current >= POST_INTERVAL) sendNow(p);
    };
    // Grab an immediate fix and post it right away so watchers see the dot instantly.
    getCurrentPosition().then((p) => onFix(p, true)).catch((err) => { setWeak(true); handleGeoErr(err); });
    watchId.current = watchPosition((p) => onFix(p), (err) => { setWeak(true); handleGeoErr(err); });
    const iv = setInterval(() => {
      const p = posRef.current;
      if (!p) return;
      if (Date.now() - lastPost.current < POST_INTERVAL) return;
      sendNow(p);
    }, 3000);
    // When the rider returns to the app after it was backgrounded, refresh immediately.
    const onVis = () => { if (document.visibilityState === 'visible' && posRef.current) sendNow(posRef.current); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearWatch(watchId.current); clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [rideStatus, id, sendNow, handleGeoErr]);

  // Manual retry — useful right after the rider grants location permission.
  const retryLocation = useCallback(() => {
    setGeoError(null);
    getCurrentPosition({ enableHighAccuracy: true, timeout: 15000, maximumAge: 0 })
      .then((p) => {
        setPos(p); posRef.current = p;
        const acceptable = p.accuracy == null || p.accuracy <= ACCURACY_LIMIT;
        setWeak(!acceptable);
        if (acceptable) lastGood.current = Date.now();
        sendNow(p);
        toast.success(t('ar_loc_found'));
      })
      .catch((err) => { handleGeoErr(err); toast.error(t('ar_loc_still_off')); });
  }, [sendNow, handleGeoErr, t]);

  const doShare = async () => {
    if (!navigator.onLine) {
      // Offline: no server link. Text current location via native SMS (no data needed).
      const pin = mapsPinFor(posRef.current || pos);
      const body = `${t('ar_share_offline_sms')}${destSuffix}${pin ? ` ${pin}` : ''}`.trim();
      toast.info(t('ar_share_offline'), { duration: 9000 });
      try { window.location.href = smsHref(ride?.trusted_contact_phone, body); }
      catch (err) { console.warn('offline share SMS failed:', err); }
      api.track('ride_shared_offline');
      return;
    }
    setShareBusy(true);
    try {
      const res = await api.createShare(id);
      if (posRef.current) api.postLocation(id, posRef.current).catch((err) => console.warn('Initial share location post failed:', err));
      setShareUrl(res.share_url); setShareOpen(true); api.track('ride_shared');
      if (res.email_status === 'success') toast.success(t('ar_sent_email'));
      else if (res.email_status === 'error') toast.warning(t('ar_email_failed'));
      await load();
      if (navigator.share) { try { await navigator.share({ title: 'My KilalaRide ride', text: 'Watch my ride live:', url: res.share_url }); } catch (e) { console.debug('share sheet dismissed:', e); } }
    } catch { toast.error(t('ar_share_err')); }
    finally { setShareBusy(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success(t('ar_copied')); };

  const savePlate = async () => {
    const v = plate.trim();
    if (!v) return;
    setPlateBusy(true);
    setRide((r) => ({ ...r, plate_number: v }));
    try {
      if (navigator.onLine) await api.setVehicle(id, { plate_number: v });
      else await cacheRide({ ...(ride || {}), plate_number: v });
      api.track('vehicle_number_added');
      toast.success(t('vp_saved_plate'));
      setDetailsDismissed(true);
    } catch (err) { console.warn('save plate failed:', err); toast.error(t('vp_fail')); }
    finally { setPlateBusy(false); }
  };

  const stopSharing = async () => {
    try {
      await api.revokeShare(id);
      toast.success(t('ar_stopped'));
      setShareUrl('');
      await load();
    } catch { toast.error(t('ar_share_err')); }
  };

  const markSafe = async () => {
    // No confirmation — one tap completes the ride and returns home immediately.
    setActiveRideId(null);
    setSosRideId(null);
    // Queue a post-ride rating prompt to show on Home (non-blocking).
    setPendingRating({ ride_id: id, plate: ride?.plate_number || null, vehicle_type: ride?.vehicle_type || null });
    navigate('/app');
    try {
      const p = posRef.current;
      if (p && navigator.onLine) { try { await api.postLocation(id, p); } catch (err) { console.warn('Final location ping failed on complete:', err); } }
      await api.updateRide(id, { status: 'completed' });
      api.track('safe_confirmed');
    } catch { toast.error(t('ar_complete_err')); }
  };

  const triggerSOS = () => {
    const p = posRef.current;
    // Mark SOS active so the safety screen locks and the app resumes here if reopened.
    setSosRideId(id);
    // Build the pre-filled SOS text and open the phone's Messages app IMMEDIATELY,
    // while still inside the tap gesture (reliable on iOS). One tap to send.
    const link = shareUrl || `${window.location.origin}/app`;
    const pin = mapsPinFor(p);
    const sosLoc = pin ? ` ${t('sms_map_pin')} ${pin}` : '';
    const smsBody = `${t('ar_sos_text_body')} ${link}${destSuffix}${sosLoc}`.trim();

    // The SMS composer is only PREPARED — the user must still tap Send. Say so
    // honestly, and never imply the alert auto-delivered.
    if (!navigator.onLine) {
      toast.error(t('ar_sos_offline'), { duration: 10000 });
    } else {
      if (p) api.postLocation(id, p).catch(() => {});
      api.addSafetyEvent(id, { event_type: 'quiet_sos', latitude: p?.latitude, longitude: p?.longitude, metadata: { share_url: shareUrl } })
        .then((res) => {
          // Report email + SMS delivery independently and honestly.
          if (res?.email_status === 'success') toast.success(t('ar_sos_sent'));
          else if (res?.email_status === 'rate_limited') toast.warning(t('ar_sos_rate'), { duration: 10000 });
          else if (res?.email_status === 'skipped_daily_cap' || res?.email_status === 'error') toast.warning(t('ar_sos_email_fail'), { duration: 10000 });
          else if (res && res.email_status == null) toast.warning(t('ar_sos_no_contact'), { duration: 9000 });
          if (res?.sms_status === 'queued') toast.info(t('ar_sos_sms_queued'));
        })
        .catch(() => { toast.error(t('ar_sos_backend_fail'), { duration: 12000 }); });
    }
    // Always prompt the manual SMS (works even offline), and tell the user to tap Send.
    toast.info(t('ar_sos_sms_prep'), { duration: 10000 });

    try { window.location.href = smsHref(ride?.trusted_contact_phone, smsBody); } catch (err) { console.warn('Could not open SOS SMS composer:', err); }
    setTimeout(() => navigate(`/app/safety/${id}`), 600);
  };

  if (!ride) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}><Loader2 className="animate-spin" style={{ color: 'var(--primary)' }} /></div>;

  if (ride.status === 'completed') {
    return (
      <div className="min-h-screen" style={{ background: 'var(--background)' }}>
        <div className="mx-auto max-w-md px-4 pt-6 pb-10 ss-fade-in">
          <div className="rounded-3xl p-6 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: 'var(--shadow)' }}>
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ background: 'var(--surface-alt)', color: 'var(--success)' }}><CheckCircle2 size={40} /></span>
            <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{t('ar_arrived')}</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{ride.public_ride_id}</p>
          </div>
          <div className="rounded-2xl p-4 border mt-4 space-y-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <Row label={t('ar_ridetype')} value={VEHICLE_LABELS[ride.vehicle_type]} />
            <Row label={t('ar_dest')} value={ride.destination_text || t('not_set')} />
            <Row label={t('ar_contact')} value={ride.trusted_contact_name || t('ar_none')} />
          </div>
          <button onClick={() => navigate('/app')} className="mt-5 w-full rounded-full font-bold min-h-[52px]" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>{t('done')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)' }}>
      <div className="mx-auto max-w-md w-full px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/app')} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}><ChevronLeft size={18} /> {t('ar_home')}</button>
          <ConnectionStatus weak={weak} testid="active-ride-connection-status" />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }}>
            <span className="w-2 h-2 rounded-full ss-pulse" style={{ background: 'var(--primary)' }} /> {t('h_ride_active')}
          </span>
          <div className="text-right">
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('ar_ridetime')}</div>
            <div className="font-bold tabular-nums" style={{ color: 'var(--text)' }}>{elapsed(ride.started_at, now)}</div>
          </div>
        </div>
        {etaText && (
          <div className="mt-2 rounded-full px-3 py-1.5 text-sm font-semibold text-center" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }} data-testid="active-ride-eta">
            🕒 {etaText}
          </div>
        )}
      </div>

      <div className="mx-auto max-w-md w-full px-4">
        {nearDest && (
          <div className="rounded-2xl p-3 mb-2 border flex items-center gap-3" style={{ background: 'color-mix(in srgb, var(--success) 12%, transparent)', borderColor: 'var(--success)' }} data-testid="active-ride-arrival-nudge">
            <ShieldCheck size={22} style={{ color: 'var(--success)', flexShrink: 0 }} />
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: 'var(--success)' }}>{t('ar_arrived_nudge_t')}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('ar_arrived_nudge_b')}</p>
            </div>
            <button onClick={markSafe} className="rounded-full px-4 min-h-[40px] text-sm font-bold" style={{ background: 'var(--success)', color: '#fff' }} data-testid="arrival-nudge-safe-button">{t('ar_safe')}</button>
          </div>
        )}
        {offRoute && !offRouteDismissed && (
          <div className="rounded-2xl p-3 mb-2 border flex items-start gap-2" style={{ background: 'color-mix(in srgb, var(--warning) 14%, transparent)', borderColor: 'var(--warning)' }} data-testid="active-ride-offroute-warn">
            <AlertTriangle size={18} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: 'var(--warning)' }}>{t('ar_offroute_t')}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('ar_offroute_b')}</p>
            </div>
            <button onClick={() => setOffRouteDismissed(true)} className="text-xs font-semibold underline" style={{ color: 'var(--text-muted)' }} data-testid="offroute-dismiss-button">{t('ar_dismiss')}</button>
          </div>
        )}
        {!online && (
          <div className="rounded-2xl p-3 mb-2 border flex items-start gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--warning)' }} data-testid="active-ride-offline-banner">
            <WifiOff size={18} style={{ color: 'var(--warning)' }} />
            <div className="text-xs" style={{ color: 'var(--text)' }}>
              <p className="font-bold">{t('ar_offline_t')}</p>
              <p style={{ color: 'var(--text-muted)' }}>{t('ar_offline_b')}</p>
            </div>
          </div>
        )}
        {(geoError === 'denied' || (geoError && !pos)) && (
          <div className="rounded-2xl p-3 mb-2 border flex items-start gap-2" style={{ background: 'color-mix(in srgb, var(--danger) 12%, transparent)', borderColor: 'var(--danger)' }} data-testid="active-ride-location-off-banner">
            <AlertTriangle size={18} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: 'var(--danger)' }} data-testid="location-off-title">{t('ar_loc_off_t')}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{geoError === 'denied' ? t('ar_loc_off_denied') : t('ar_loc_off_b')}</p>
            </div>
            <button onClick={retryLocation} className="rounded-full px-4 min-h-[40px] text-sm font-bold shrink-0" style={{ background: 'var(--danger)', color: '#fff' }} data-testid="location-retry-button">{t('ar_loc_retry')}</button>
          </div>
        )}
        <div style={{ height: '46vh' }} className="rounded-2xl overflow-hidden border">
          <MapView position={pos} path={path} route={routeCoords} origin={originPt} destination={destPt} testid="active-ride-map" />
        </div>
        {pos && (
          <p className="text-[11px] mt-1 flex items-center gap-1 tabular-nums" style={{ color: 'var(--text-muted)' }} data-testid="active-ride-coords">
            <MapPin size={12} /> {pos.latitude.toFixed(5)}, {pos.longitude.toFixed(5)}
            {pos.recorded_at ? ` · ${timeAgo(pos.recorded_at, now)}` : ''}
          </p>
        )}
      </div>

      <div className="mx-auto max-w-md w-full px-4 mt-3" data-testid="active-ride-details-sheet">
        {!ride.plate_number && !(ride.photos && ride.photos.length) && !detailsDismissed && (
          <div className="rounded-2xl p-3 border mb-2" style={{ background: 'var(--surface)', borderColor: 'var(--primary)' }} data-testid="active-ride-add-details-card">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Hash size={16} style={{ color: 'var(--primary)' }} />
                <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{t('ar_add_details_t')}</span>
              </div>
              <button onClick={() => setDetailsDismissed(true)} className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }} data-testid="add-details-later-button">{t('ar_later')}</button>
            </div>
            <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{t('ar_add_details_b')}</p>
            <div className="flex items-center gap-2">
              <input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder={t('vp_plate_ph')} autoCapitalize="characters" className="flex-1 rounded-xl px-3 min-h-[44px] border text-sm bg-transparent outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} data-testid="active-ride-plate-input" />
              <button onClick={savePlate} disabled={plateBusy || !plate.trim()} className="rounded-xl px-4 min-h-[44px] text-sm font-bold disabled:opacity-50" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="active-ride-plate-save">{plateBusy ? <Loader2 size={14} className="animate-spin" /> : t('vp_save_plate')}</button>
            </div>
            <button onClick={() => navigate(`/app/ride/${id}/photo`)} className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl font-semibold min-h-[44px] border text-sm" style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--text)' }} data-testid="active-ride-add-photo-link">
              <Camera size={16} style={{ color: 'var(--primary)' }} /> {t('ar_add_photo')} <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
        )}
        <div className="rounded-2xl p-3 border flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            {ride.photos?.[0] && <img src={ride.photos[0].data_url} alt="Vehicle" className="w-12 h-12 rounded-xl object-cover border" style={{ borderColor: 'var(--border)' }} />}
            <div>
              <div className="text-sm font-bold tabular-nums" style={{ color: 'var(--text)' }} data-testid="active-ride-ride-id">{ride.public_ride_id}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{VEHICLE_LABELS[ride.vehicle_type]}{ride.destination_text ? ` → ${ride.destination_text}` : ''}</div>
            </div>
          </div>
          {ride.trusted_contact_name && <div className="text-xs text-right" style={{ color: 'var(--text-muted)' }}>{t('ar_watching')}<br /><span className="font-semibold" style={{ color: 'var(--text)' }}>{ride.trusted_contact_name}</span></div>}
        </div>
        {ride.contact_notified_at && (
          <div className="mt-2 flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold" style={{ background: 'color-mix(in srgb, var(--success) 12%, transparent)', color: 'var(--success)' }} data-testid="active-ride-email-notified">
            <MailCheck size={15} />
            <span>{t('ar_email_notified')}</span>
            <span className="font-normal" style={{ color: 'var(--text-muted)' }}>· {timeAgo(ride.contact_notified_at, now)}</span>
          </div>
        )}
        {!ride.trusted_contact_email && (
          <div className="mt-2 flex items-start gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold" style={{ background: 'color-mix(in srgb, var(--warning) 14%, transparent)', color: 'var(--warning)' }} data-testid="active-ride-no-contact-warn">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>{t('ar_no_contact_warn')}</span>
          </div>
        )}
      </div>

      <div className="mx-auto max-w-md w-full px-4 mt-3 pb-6 space-y-2">
        <button data-testid="active-ride-share-button" onClick={doShare} disabled={shareBusy} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl font-bold min-h-[56px] text-base" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
          {shareBusy ? <Loader2 size={20} className="animate-spin" /> : <Share2 size={20} />} {ride.has_active_share ? t('ar_reshare') : t('ar_share')}
        </button>
        {ride.has_active_share && (
          <button data-testid="active-ride-stop-sharing-button" onClick={stopSharing} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl font-semibold min-h-[48px] text-sm border" style={{ background: 'var(--surface)', color: 'var(--text)', borderColor: 'var(--border)' }}>
            <EyeOff size={18} /> {t('ar_stop_sharing')}
          </button>
        )}
        <button data-testid="active-ride-im-safe-button" onClick={markSafe} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl font-bold min-h-[56px] text-base border" style={{ background: 'var(--surface)', color: 'var(--success)', borderColor: 'var(--success)' }}>
          <ShieldCheck size={20} /> {t('ar_safe')}
        </button>
        <HoldSOSButton onTrigger={triggerSOS} />
        {!online && <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>{t('ar_offline')}</p>}
      </div>

      <ConfirmModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={t('ar_share_title')}
        description={t('ar_share_sub')}
      >
        <div className="rounded-xl p-2 border flex items-center gap-2" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }}>
          <input readOnly value={shareUrl} className="flex-1 bg-transparent text-sm outline-none" style={{ color: 'var(--text)' }} data-testid="share-link-input" />
          <button onClick={copyLink} className="inline-flex items-center gap-1 rounded-lg px-3 min-h-[44px] text-sm font-semibold" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}><Copy size={16} /> {t('ar_copy')}</button>
        </div>
        <a
          href={smsHref(ride.trusted_contact_phone, `${t('ar_sms_body')} ${shareUrl}${destSuffix}${locSuffix}`)}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold min-h-[48px] border"
          style={{ borderColor: 'var(--primary)', color: 'var(--primary)', background: 'var(--surface)' }}
          data-testid="share-text-contact"
        >
          <MessageCircle size={18} /> {t('ar_text_contact')}
        </a>
        <p className="text-xs mt-1 text-center" style={{ color: 'var(--text-muted)' }}>{t('ar_text_note')}</p>
      </ConfirmModal>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
    <span className="font-semibold" style={{ color: 'var(--text)' }}>{value}</span>
  </div>
);
